import { graphql, useStaticQuery } from 'gatsby';
import { useMemo } from 'react';
import {
  applyMachinesMenuCategories,
  buildProductTree,
  type ProductCategoryFlat,
  type ProductTreeNode,
  type ProductTreeProduct,
  buildParentMap,
  getCategoryPath,
} from '../../lib/product-tree';

interface ProductTreeQuery {
  allStrapiProductCategory: { nodes: ProductCategoryFlat[] };
  allStrapiProduct: {
    nodes: {
      Slug: string;
      Title: string;
      ProductName?: string | null;
      SeriesName?: string | null;
      HeaderImage?: { url: string };
      Thumbnail?: { url: string };
      ProductCategory?: { Slug: string } | null;
    }[];
  };
  strapiMachinesPage?: {
    MachineCategories?: {
      position?: number | null;
      Category?: { Slug: string } | null;
    }[] | null;
  } | null;
}

export interface ProductTreeData {
  roots: ProductTreeNode[];
  categories: ProductCategoryFlat[];
  products: ProductTreeProduct[];
  productsByCategorySlug: Map<string, ProductTreeProduct[]>;
}

export const useProductTree = (): ProductTreeData => {
  const data = useStaticQuery<ProductTreeQuery>(graphql`
    query ProductTreeQuery {
      allStrapiProductCategory(sort: { position: ASC }) {
        nodes {
          Slug
          Title
          MenuLabel
          position
          PreviewImage {
            url
          }
          parent {
            ... on StrapiProductCategory {
              Slug
            }
          }
        }
      }
      allStrapiProduct(
        filter: { ProductCategory: { Slug: { ne: null } } }
        sort: { position: ASC }
        limit: 1000
      ) {
        nodes {
          Slug
          Title
          ProductName
          SeriesName
          HeaderImage {
            url
          }
          Thumbnail {
            url
          }
          ProductCategory {
            Slug
          }
        }
      }
      strapiMachinesPage {
        MachineCategories {
          position
          Category {
            Slug
          }
        }
      }
    }
  `);

  return useMemo(() => {
    const categories = data.allStrapiProductCategory?.nodes ?? [];
    const parentMap = buildParentMap(categories);
    const machineMenuSlugs = [...(data.strapiMachinesPage?.MachineCategories ?? [])]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .map((item) => item.Category?.Slug)
      .filter((slug): slug is string => Boolean(slug));

    const roots = applyMachinesMenuCategories(
      buildProductTree(categories),
      machineMenuSlugs,
    );

    const products: ProductTreeProduct[] = (
      data.allStrapiProduct?.nodes ?? []
    )
      .filter((p) => p.ProductCategory?.Slug)
      .map((p) => {
        const categorySlug = p.ProductCategory!.Slug;
        const categoryPath = getCategoryPath(categorySlug, parentMap);
        return {
          slug: p.Slug,
          title: p.Title,
          productName: p.ProductName?.trim() || undefined,
          seriesName: p.SeriesName?.trim() || undefined,
          categorySlug,
          categoryPath,
          headerImageUrl: p.HeaderImage?.url,
          thumbnailUrl: p.Thumbnail?.url,
        };
      });

    const productsByCategorySlug = new Map<string, ProductTreeProduct[]>();
    for (const product of products) {
      const list = productsByCategorySlug.get(product.categorySlug) ?? [];
      list.push(product);
      productsByCategorySlug.set(product.categorySlug, list);
    }

    return {
      roots,
      categories,
      products,
      productsByCategorySlug,
    };
  }, [data]);
};
